import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { ListRow } from "components/designV2/listRow/listRow";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { PaymentMethod } from "model/Gig";
import { Api } from "services/api/api.service";
import type { StaffItem } from "services/api/company/staff.service";
import { Checkbox } from "web-apps/company/components/general/checkbox/checkbox.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type { Worker } from "./chooseWorkersModal.types";
import { WorkerTeamSummary } from "./workerTeamSummary.component";
import {
  ActionWrap,
  CheckboxWrap,
  LoaderWrap,
  RowActions,
  SearchWrap,
  SimflexityPayStyledLogo,
  StyledLoader,
  WorkerList,
} from "./chooseWorkersModal.styled";

type Props = {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  onClick: (checked: Worker[]) => void;
  title: string;
  info: string;
  buttonTitle: string;
  selected?: Worker[];
  filter: string;
  showPaymentMethod?: boolean;
};

const getStaffPaymentType = (staffItem: StaffItem): string => {
  return staffItem.paymentType ?? "";
};

export const ChooseWorkersModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [workerList, setWorkerList] = useState<Worker[]>([]);
  const [companyState] = useCompanyStore();
  const [checked, setChecked] = useState<Worker[]>(
    props.selected && props.selected.length > 0 ? props.selected : [],
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setChecked(
      props.selected && props.selected.length > 0 ? props.selected : [],
    );
  }, [props.selected]);

  const filteredWorkers = useMemo(() => {
    if (!inputValue) return workerList;

    return workerList.filter((worker) =>
      `${worker.firstName} ${worker.lastName}`
        .toLowerCase()
        .includes(inputValue.toLowerCase()),
    );
  }, [workerList, inputValue]);

  const toWorker = (staffItem: StaffItem): Worker => {
    return {
      id: staffItem.workerId,
      workerId: staffItem.workerId,
      externalId: staffItem.externalId ?? "",
      firstName: staffItem.firstName ?? "",
      lastName: staffItem.lastName ?? "",
      personalIdentityNumber: staffItem.personalIdentityNumber ?? "",
      employmentInfo: {
        current: {
          employmentType: staffItem.employmentType ?? "",
          paymentType: getStaffPaymentType(staffItem),
          role: staffItem.role ?? undefined,
        },
      },
      workGroups: staffItem.workGroups ?? [],
      selectedTeamId:
        staffItem.workGroupId && staffItem.workGroupId > 0
          ? staffItem.workGroupId
          : staffItem.workGroups?.length === 1 &&
              (staffItem.workGroups[0]?.workGroupId ?? 0) > 0
            ? staffItem.workGroups[0].workGroupId
            : undefined,
    };
  };

  const getCompanyWorkers = useCallback(async (): Promise<void> => {
    if (!companyState.company?.id) return;

    setIsLoading(true);
    const pageSize = 50;
    let allWorkers: Worker[] = [];
    let currentPage = 0;
    let hasMoreData = true;

    try {
      while (hasMoreData) {
        const res = await Api().company.staff.getStaff(
          companyState.company.id,
          {
            page: currentPage,
            pageSize,
            status: "Active",
            workGroupFilter:
              props.filter === "InWorkGroup" ? "InWorkGroup" : "All",
          },
        );

        const nextWorkers = (res.data.items ?? []).map(toWorker);
        allWorkers = [...allWorkers, ...nextWorkers];

        if (nextWorkers.length < pageSize) {
          hasMoreData = false;
        } else {
          currentPage++;
        }
      }

      setWorkerList(allWorkers);
    } catch {
      setWorkerList([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyState.company?.id, props.filter]);

  useEffect(() => {
    getCompanyWorkers();
  }, [getCompanyWorkers]);

  return (
    <Modal
      label={props.title}
      size="small"
      isOpen={props.showModal}
      onClose={() => props.setShowModal(false)}
    >
      <Text typography="b2" color={ColorV2.Blue2}>
        {props.info}
      </Text>
      <SearchWrap>
        <InputField
          value={inputValue}
          onChange={setInputValue}
          placeholder={t("Schedule.Search")}
          fullWidth
          iconName="v2/search"
          iconPosition="left"
          onClear={() => setInputValue("")}
        />
      </SearchWrap>
      {!isLoading ? (
        <WorkerList>
          {filteredWorkers.length > 0 &&
            filteredWorkers.map((worker) => (
              <ListRow
                key={worker.workerId}
                variant="dark"
                selected={checked.some((w) => w.workerId === worker.workerId)}
                onClick={() =>
                  setChecked((prev) =>
                    prev.some((w) => w.workerId === worker.workerId)
                      ? prev.filter((w) => w.workerId !== worker.workerId)
                      : [...prev, worker],
                  )
                }
              >
                <AvatarChip
                  title={`${worker.firstName} ${worker.lastName}`}
                  subTitle={
                    <WorkerTeamSummary
                      workGroups={worker.workGroups}
                      emptyLabel={t("Schedule.UnknownTeam")}
                    />
                  }
                  avatarUrl={
                    worker.externalId &&
                    companyState.companyWorkerImages?.[worker.externalId] &&
                    companyState.companyWorkerImages?.[worker.externalId] !==
                      "no-image"
                      ? companyState.companyWorkerImages?.[worker.externalId]
                      : undefined
                  }
                />
                <RowActions>
                  {worker.employmentInfo.current.paymentType ===
                    PaymentMethod.SIMFLEXITY_PAY &&
                    props.showPaymentMethod && (
                      <SimflexityPayStyledLogo
                        src={SimflexityPayLogo}
                        alt="SimflexityPay logo"
                      />
                    )}
                  <CheckboxWrap onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                      name={worker.workerId.toString()}
                      checked={checked.some(
                        (w) => w.workerId === worker.workerId,
                      )}
                      onChange={(isChecked) =>
                        setChecked((prev) =>
                          isChecked
                            ? [...prev, worker]
                            : prev.filter(
                                (w) => w.workerId !== worker.workerId,
                              ),
                        )
                      }
                    />
                  </CheckboxWrap>
                </RowActions>
              </ListRow>
            ))}
        </WorkerList>
      ) : (
        <LoaderWrap>
          <StyledLoader />
        </LoaderWrap>
      )}
      <ActionWrap>
        <PrimaryButton
          size="small"
          onClick={() => props.onClick(checked)}
          disabled={checked.length === 0}
          label={props.buttonTitle}
        />
      </ActionWrap>
    </Modal>
  );
};
